import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from "@angular/core";

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import {
  FormArray,
  FormControl,
  FormGroup,
  FormGroupDirective,
} from "@angular/forms";
import {
  ChildEvent,
  ChildEventPriceChange,
} from "../event-page-models/child-event";
import { EventFormInitialData } from "../event-page-models/event-form-initial-data";
import { EventFormStatusEnum } from "../event-page-models/event-form-status.enum";
import { EventsService } from "../../../_services/eventsServices/events.service";
import { sleep } from "../../../_helpers/sleep";
import { EventSerializerService } from "../../../_services/eventsServices/eventSerializer.service";
import { CreateParentEventFormPricingRowComponent } from "../create-parent-event-form-pricing-row/create-parent-event-form-pricing-row.component";
import { HebrewNumbering } from "../utils/hebrewNumbering";
import { EventFormCustomFormType } from "../event-page-models/event-form-custom-form-type";

@Component({
  selector: "create-parent-event-form-pricing-list",
  templateUrl: "./create-parent-event-form-pricing-list.component.html",
  styleUrls: ["create-parent-event-form-pricing-list.component.scss"],
  animations: [
    trigger("openClose", [
      state(
        "open",
        style({
          height: "500px",
          maxHeight: "2000px",
          overflow: "visible",
          opacity: 1,
        })
      ),
      state(
        "closed",
        style({
          height: "1px",
          opacity: 0,
          overflow: "hidden",
        })
      ),
      transition("open => closed", [animate("0.2s")]),
      transition("* => open", [animate("0.2s")]),
      transition("closed => open", [animate("0.2s")]),
    ]),
  ],
})
export class CreateParentEventFormPricingListComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input("eventFormCustomFormType")
  public eventFormCustomFormType: EventFormCustomFormType =
    EventFormCustomFormType.common;
  public ChildEventList: ChildEvent[] = [];
  public AllChildEventsFormGroup: FormGroup;
  @ViewChildren(CreateParentEventFormPricingRowComponent)
  public pricingRowsComponents: QueryList<CreateParentEventFormPricingRowComponent>;

  constructor(
    private formInput: FormGroupDirective,
    private eventsService: EventsService
  ) {
    this.eventsService.eventFormInitialData$
      .pipe()
      .subscribe((eventFormInitialData: EventFormInitialData) => {
        if (!eventFormInitialData) {
          return;
        }

        if (
          eventFormInitialData.formStatus === EventFormStatusEnum.Update ||
          eventFormInitialData.formStatus === EventFormStatusEnum.Copy
        ) {
          this.patchFormValues(eventFormInitialData).then();
        }
      });
  }

  public get canAddMorePricingRows() {
    return this.eventFormCustomFormType !== EventFormCustomFormType.Michlol;
  }

  get activeChildEventsControls() {
    return (this.AllChildEventsFormGroup.get("ActiveChildEvents") as FormArray)
      .controls as FormGroup[];
  }

  public getHebrewFromIndex(i: number) {
    return this.ChildEventList.length > 1 ? HebrewNumbering[i] + "'" : "";
  }

  public ngOnInit(): void {
    this.addToForm();
    if (this.ChildEventList.length === 0) {
      this.addChildEvent();
    }
  }

  public ngAfterViewInit() {}

  public ngOnDestroy(): void {}

  public addChildEvent() {
    const blankChildEvent: ChildEvent = {
      registrationEndDate: undefined,
      registrationStartDate: undefined,
      brotherDiscount: false,
      childEventCode: "0",
      childEventId: 0,
      childEventPrice: 0,
      childEventPriceChange: [],
      circle: [],
      closedToMembers: false,
      eventEndDate: "",
      eventEndHour: "",
      eventIndex: "",
      eventName: "",
      eventOpenTo: "",
      eventStartDate: "",
      eventStartHour: "",
      eventStatus: undefined,
      eventType: undefined,
      parentEventCode: "0",
      parentEventId: 0,
      gainCenter1: "",
      gainCenter2: "",
      layers: [],
      nests: [],
      prices: "",
      registeredChildren: 0,
      eventCollection: [],
    };
    this.ChildEventList.push(blankChildEvent);

    const formArr = this.AllChildEventsFormGroup.get(
      "ActiveChildEvents"
    ) as FormArray;
    formArr.push(new FormGroup({}));
  }

  public async addChildEventClick() {
    this.addChildEvent();
    await sleep(50);
    if (this.pricingRowsComponents) {
      this.pricingRowsComponents.forEach((pricingRow, i) => {
        const isLast = this.pricingRowsComponents.length - 1 === i;
        pricingRow.forceSetOpenClose(isLast);
      });
    }
  }

  public deletePricingRow($event: number, i: number) {
    const formArr = this.AllChildEventsFormGroup.get(
      "ActiveChildEvents"
    ) as FormArray;
    if (formArr.length <= 1) {
      return;
    }
    const eventToDelete = formArr.value[i];
    formArr.removeAt(i);
    this.ChildEventList.splice(i, 1);
    const deletedChildEvents = this.AllChildEventsFormGroup.get(
      "DeletedChildEvents"
    ) as FormControl;

    if (eventToDelete.id && +eventToDelete.id > 0) {
      const deletedChildEventsValue = deletedChildEvents.value;
      deletedChildEventsValue.push(eventToDelete.id);
      deletedChildEvents.setValue(deletedChildEventsValue);
    }
  }

  private addToForm() {
    this.AllChildEventsFormGroup = new FormGroup({
      ActiveChildEvents: new FormArray([]),
      DeletedChildEvents: new FormControl([]),
    });
    this.formInput.form.addControl(
      "AllChildEvents",
      this.AllChildEventsFormGroup
    );
  }

  private async patchFormValues(eventFormInitialData: EventFormInitialData) {
    await sleep(100);
    const childEvents =
      eventFormInitialData.eventFormParentEventData.child_events;
    const numberOfPricingRows = (
      this.AllChildEventsFormGroup.get("ActiveChildEvents") as FormArray
    ).controls.length;
    const pricingRowsToAdd = childEvents.length - numberOfPricingRows;
    for (let ic = 0; ic < pricingRowsToAdd; ic++) {
      this.addChildEvent();
    }
    await sleep(50);
    childEvents.forEach((childEvent, i) => {
      const control = (
        this.AllChildEventsFormGroup.get("ActiveChildEvents") as FormArray
      ).controls[i];
      const childEventCollectionSelectedOptions = childEvent.event_collections
        .filter((e) => e.is_selected)
        .map(
          EventSerializerService.serverEventCollectionItemToSelectMenuOption
        );
      if (control) {
        const childEventPatchValues = {
          id: childEvent.id,
          eventPrice: childEvent.price,
          can_be_deleted: childEvent.can_be_deleted,
          eventFormMaagal: [""],
          eventFormLayers: childEvent.grades.map((g) => g.is_selected),
          layers: childEvent.grades.map((g) => ({
            layerId: g.id,
            layerName: g.name,
            value: g.is_selected,
          })),
          eventFormPriceChanges: [],
          organs: [],
          circles: [],
          childEventAdvancedDetails: {
            eventCollection: childEventCollectionSelectedOptions,
            eventDescription: childEvent.description,
            eventCode: childEvent.code,
            eventStartDate:
              childEvent.event_start_date &&
              EventSerializerService.EpocToDateAndTime(
                childEvent.event_start_date
              ).datePicker,
            eventStartHour:
              childEvent.event_start_date &&
              EventSerializerService.EpocToDateAndTime(
                childEvent.event_start_date
              ).hoursPicker,
            eventEndDate:
              childEvent.event_end_date &&
              EventSerializerService.EpocToDateAndTime(
                childEvent.event_end_date
              ).datePicker,
            eventEndHour:
              childEvent.event_end_date &&
              EventSerializerService.EpocToDateAndTime(
                childEvent.event_end_date
              ).hoursPicker,
            registrationStartDate:
              childEvent.registration_start_date &&
              EventSerializerService.EpocToDateAndTime(
                childEvent.registration_start_date
              ).datePicker,
            registrationEndDate:
              childEvent.registration_end_date &&
              EventSerializerService.EpocToDateAndTime(
                childEvent.registration_end_date
              ).datePicker,
            closedToMembersChildEvent: childEvent.tax_payers_only,
            brothersDiscountChildEvent: childEvent.has_brother_discount,
          },
        };
        control.patchValue(childEventPatchValues);
      }
    });
    await sleep(100);
    if (this.pricingRowsComponents) {
      this.pricingRowsComponents.forEach((pricingRow, i) => {
        const childEvent = childEvents[i];
        if (!childEvent) {
          return;
        }
        const pricingChange: ChildEventPriceChange = {
          id: i,
          priceChangeDate:
            childEvent.price_increase_date &&
            EventSerializerService.EpocToDateAndTime(
              childEvent.price_increase_date
            ).datePicker,
          priceChangeNewPrice: +childEvent.price_after_increase || 0,
        };
        if (
          pricingChange.priceChangeNewPrice &&
          pricingChange.priceChangeDate
        ) {
          pricingRow.updatePriceChanges("patchNew", pricingChange);
        }
        pricingRow.layerClicked();
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/events-screen/create-parent-event-form-pricing-list/create-parent-event-form-pricing-list.component.ts